import { CartAddEvent } from '@theme/events';

/**
 * <cart-upsell-component>
 *
 * Hybrid upsell strip — lives inside the cart drawer.
 *
 * Two runtime branches based on data-mode:
 *   - server | demo : cards are pre-rendered server-side. No JS hydration.
 *   - api           : cards are hydrated from the Shopify Recommendations
 *                     API on the current cart's most recent item, then
 *                     re-hydrated whenever a new item is added.
 *
 * ATC form interception is handled GLOBALLY by assets/product-card-ajax.js
 * (Step 23.5) — there is no element-scoped submit listener here. The
 * global handler dispatches CartAddEvent on success, this component's
 * #handleCartUpdate listener picks it up and re-hydrates the strip with
 * recommendations for the just-added product.
 */
class CartUpsellComponent extends HTMLElement {
  /** @type {Map<string, string>} productId → cached cards HTML */
  #cache = new Map();

  /** @type {string | null} */
  #lastHydratedProductId = null;

  /** @type {boolean} */
  #pendingHydration = false;

  /** @type {string} */
  #mode = 'api';

  connectedCallback() {
    this.#mode = this.dataset.mode || 'api';

    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);

    if (this.#mode === 'api') {
      const seedId = this.dataset.lastProductId;
      if (seedId) this.#hydrate(seedId);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
  }

  /**
   * Re-hydrate when a new item is added (API mode only).
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    if (this.#mode !== 'api') return;

    const detail = /** @type {any} */ (event).detail;
    const productId = detail?.data?.productId ? String(detail.data.productId) : null;
    if (!productId) return;
    if (productId === this.#lastHydratedProductId) return;

    this.#hydrate(productId);
  };

  /**
   * Fetch (or cache-hit) recommendations for the given product and
   * inject the cards into the track.
   * @param {string} productId
   */
  async #hydrate(productId) {
    if (this.#pendingHydration) return;
    this.#lastHydratedProductId = productId;

    if (this.#cache.has(productId)) {
      this.#renderCards(this.#cache.get(productId) || '');
      return;
    }

    this.#pendingHydration = true;

    const sectionId = this.dataset.sectionId || 'api-cart-upsell';
    const limit = parseInt(this.dataset.limit || '3', 10);
    const url = `/recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=related`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.#hide();
        return;
      }
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const slot = parsed.querySelector('[data-cart-upsell-cards]');
      const cardsHtml = slot ? slot.innerHTML.trim() : '';

      this.#cache.set(productId, cardsHtml);

      if (!cardsHtml) {
        this.#hide();
      } else {
        this.#renderCards(cardsHtml);
      }
    } catch (error) {
      console.error('[cart-upsell] hydration failed', error);
      this.#hide();
    } finally {
      this.#pendingHydration = false;
    }
  }

  /** @param {string} html */
  #renderCards(html) {
    const track = this.querySelector('[data-cart-upsell-track]');
    if (!track) return;
    track.innerHTML = html;
    this.removeAttribute('hidden');
  }

  #hide() {
    this.setAttribute('hidden', '');
  }

}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
